/**
 * Lever 2: right-sizing and format switching.
 * For each single-product parcel, finds the packaging format from the catalogue
 * that fits the product and gives the lowest postage plus packaging cost.
 * Multi-product parcels are left for manual review at launch.
 */
import { findService, priceParcel, sortedSides, type RateCard } from "./ratecard.ts";
import type { Dimensions, Parcel, Pence } from "./types.ts";
import { round } from "./units.ts";

export interface PackagingFormat {
  code: string;
  name: string;
  kind: "box" | "mailer" | "envelope";
  /** Usable space inside */
  innerDims: Dimensions;
  /** Size the carrier measures */
  outerDims: Dimensions;
  tareWeightKg: number;
  unitCostPence: Pence;
}

export interface PackagingCatalogue {
  id: string;
  version: string;
  formats: PackagingFormat[];
}

/** Product size and weight before packing, keyed by SKU. Supplied by the client or measured. */
export interface ProductSpec {
  sku: string;
  dims: Dimensions;
  weightKg: number;
}

export interface Lever2Options {
  /** Space needed around the product on each side, in cm (default 1) */
  clearanceCm?: number;
  /** The client's current cost per parcel for packaging, if known; enables a net saving */
  currentPackagingPence?: Pence | null;
}

export type Lever2Status = "saving" | "no_better_format" | "not_assessed";

export interface Lever2ParcelResult {
  orderId: string;
  sku: string | null;
  status: Lever2Status;
  /** Why the parcel was not assessed */
  reason?: "multi_item" | "no_items" | "missing_product_spec" | "unknown_service" | "current_not_priced" | "no_format_fits";
  currentBandCode: string | null;
  currentPostagePence: Pence | null;
  proposed: {
    formatCode: string;
    formatName: string;
    bandCode: string;
    postagePence: Pence;
    packagingPence: Pence;
    weightKg: number;
  } | null;
  postageSavingPence: Pence;
  /** Postage saving less any increase in packaging cost; null when current packaging cost is unknown */
  netSavingPence: Pence | null;
}

export interface Lever2Summary {
  parcels: number;
  assessed: number;
  savingCount: number;
  postageSavingPence: Pence;
  netSavingPence: Pence | null;
  notAssessed: Record<string, number>;
  /** How many parcels would move to each format */
  byFormat: Record<string, number>;
}

/** True when the product, plus clearance on every side, fits inside the format. */
export function fitsInside(product: Dimensions, inner: Dimensions, clearanceCm: number): boolean {
  const p = sortedSides(product);
  const i = sortedSides(inner);
  return p.every((side, k) => side + 2 * clearanceCm <= i[k]! + 1e-9);
}

export function rightSizeParcel(
  p: Parcel,
  cards: RateCard[],
  catalogue: PackagingCatalogue,
  specs: Map<string, ProductSpec>,
  opts: Lever2Options = {},
): Lever2ParcelResult {
  const clearance = opts.clearanceCm ?? 1;
  const currentPack = opts.currentPackagingPence ?? null;
  const out: Lever2ParcelResult = {
    orderId: p.orderId,
    sku: null,
    status: "not_assessed",
    currentBandCode: null,
    currentPostagePence: null,
    proposed: null,
    postageSavingPence: 0,
    netSavingPence: null,
  };

  if (p.items.length === 0) return { ...out, reason: "no_items" };
  const item = p.items[0]!;
  if (p.items.length > 1 || item.qty !== 1) return { ...out, sku: item.sku, reason: "multi_item" };
  out.sku = item.sku;
  const spec = specs.get(item.sku);
  if (!spec) return { ...out, reason: "missing_product_spec" };

  const found = findService(cards, p.carrierService);
  if (!found) return { ...out, reason: "unknown_service" };
  const current = priceParcel(p.dims, p.actualWeightKg, found.service);
  if (!current) return { ...out, reason: "current_not_priced" };
  out.currentBandCode = current.band.code;
  out.currentPostagePence = current.band.pricePence;

  let best: NonNullable<Lever2ParcelResult["proposed"]> | null = null;
  for (const f of catalogue.formats) {
    if (!fitsInside(spec.dims, f.innerDims, clearance)) continue;
    const weightKg = round(spec.weightKg + f.tareWeightKg, 3);
    const priced = priceParcel(f.outerDims, weightKg, found.service);
    if (!priced) continue;
    const total = priced.band.pricePence + f.unitCostPence;
    const bestTotal = best ? best.postagePence + best.packagingPence : Infinity;
    if (total < bestTotal) {
      best = {
        formatCode: f.code,
        formatName: f.name,
        bandCode: priced.band.code,
        postagePence: priced.band.pricePence,
        packagingPence: f.unitCostPence,
        weightKg,
      };
    }
  }
  if (!best) return { ...out, reason: "no_format_fits" };

  const postageSaving = current.band.pricePence - best.postagePence;
  if (postageSaving <= 0) return { ...out, status: "no_better_format", proposed: best };

  const net = currentPack === null ? null : postageSaving - (best.packagingPence - currentPack);
  if (net !== null && net <= 0) {
    return { ...out, status: "no_better_format", proposed: best, postageSavingPence: postageSaving, netSavingPence: net };
  }
  return { ...out, status: "saving", proposed: best, postageSavingPence: postageSaving, netSavingPence: net };
}

export function runLever2(
  parcels: Parcel[],
  cards: RateCard[],
  catalogue: PackagingCatalogue,
  products: ProductSpec[],
  opts: Lever2Options = {},
) {
  const specs = new Map(products.map((s) => [s.sku, s]));
  const results = parcels.map((p) => rightSizeParcel(p, cards, catalogue, specs, opts));
  const summary: Lever2Summary = {
    parcels: results.length,
    assessed: 0,
    savingCount: 0,
    postageSavingPence: 0,
    netSavingPence: opts.currentPackagingPence == null ? null : 0,
    notAssessed: {},
    byFormat: {},
  };
  for (const r of results) {
    if (r.status === "not_assessed") {
      const k = r.reason ?? "unknown";
      summary.notAssessed[k] = (summary.notAssessed[k] ?? 0) + 1;
      continue;
    }
    summary.assessed++;
    if (r.status !== "saving" || !r.proposed) continue;
    summary.savingCount++;
    summary.postageSavingPence += r.postageSavingPence;
    if (summary.netSavingPence !== null) summary.netSavingPence += r.netSavingPence ?? 0;
    summary.byFormat[r.proposed.formatCode] = (summary.byFormat[r.proposed.formatCode] ?? 0) + 1;
  }
  return { results, summary };
}
